import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography
} from '@mui/material';
import ActionButton from './ActionButton';
import { useTheme } from '@mui/material/styles';

import { delayedLockWrapCreator } from './locks';
import { formatApiError } from './formatApiError';

/**
 * AsyncFormDialog — shared scaffolding for every dialog in the admin flow
 * that wraps an async submission. Replaces the hand-rolled state +
 * try/catch + dispatch-lock + error-coercion blocks that lived in
 * MqttSetupDialog, CreateDatasetDialog, and ConfirmActionDialog.
 *
 * Responsibilities:
 *  - Manages `submitting` state and a submittingRef gate (avoids the
 *    double-fire race when two clicks land in the same React tick before
 *    the disabled state propagates).
 *  - Catches submission errors, runs them through formatApiError, and
 *    renders them in an <Alert role="alert"> so screen readers announce.
 *  - Holds the dispatch lock around the close transition.
 *  - Renders the consistent dialog title / DialogActions footer with
 *    contained primary button and text-style Cancel (DES15).
 *  - Optionally requires the user to type a confirmation phrase (the
 *    dataset name) before enabling the primary button — for high-blast-
 *    radius destructive actions (DES2). Set `confirmText` to the required
 *    phrase; the dialog renders a TextField and disables submit until it
 *    matches.
 *
 * Props
 * -----
 * - open, onClose: standard
 * - title: string
 * - subtitle?: string|node — small line under the title
 * - children: form contents (rendered between title and actions)
 * - submitLabel: primary button label
 * - submitColor: 'primary' | 'error' | etc — default 'primary'
 * - submitIcon?: ReactNode — optional <SaveIcon/> etc shown via
 *   loadingPosition="start" so the verb stays visible during save
 * - cancelLabel: default 'Cancel'
 * - canSubmit?: bool — extra precondition gate from caller (form
 *   validation). Defaults to true.
 * - onSubmit: async () => any — throws on failure, will be caught
 * - onSuccess?: () => void — called after onSubmit resolves cleanly,
 *   before onClose. Use this to fire a Snackbar.
 * - confirmText?: string — see above. When set, an extra TextField is
 *   rendered and the primary button stays disabled until the field
 *   exactly equals confirmText.
 * - confirmTextLabel?: string — default 'Type to confirm'
 * - dialogProps?: passthrough to the underlying Dialog (fullWidth, etc)
 */
export default function AsyncFormDialog({
    open,
    onClose,
    title,
    subtitle,
    children,
    submitLabel,
    submitColor = 'primary',
    submitIcon,
    cancelLabel = 'Cancel',
    canSubmit = true,
    onSubmit,
    onSuccess,
    confirmText,
    confirmTextLabel = 'Type to confirm',
    dialogProps = {}
}) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [confirmInput, setConfirmInput] = useState('');
    const [dispatchLock, setDispatchLock] = useState(false);
    const submittingRef = useRef(false);
    const theme = useTheme();
    const dispatchLockWrap = delayedLockWrapCreator(
        dispatchLock,
        setDispatchLock,
        theme.transitions.duration.leavingScreen
    );

    // Reset error + confirm text whenever the dialog re-opens. Prevents a
    // previous failure's Alert sticking around when the user reopens.
    useEffect(() => {
        if (open) {
            setError('');
            setConfirmInput('');
        }
    }, [open]);

    const confirmRequired = typeof confirmText === 'string' && confirmText.length > 0;
    const confirmMatches = !confirmRequired || confirmInput === confirmText;
    const primaryEnabled = !submitting && canSubmit && confirmMatches;

    const handleSubmit = async () => {
        // Belt and braces: ref guard for the in-tick double-click race
        // that disabled-state alone can't catch.
        if (submittingRef.current) return;
        if (!primaryEnabled) return;
        submittingRef.current = true;
        setSubmitting(true);
        setError('');
        await dispatchLockWrap(async () => {
            try {
                await onSubmit();
                if (typeof onSuccess === 'function') onSuccess();
                onClose();
            } catch (err) {
                console.error('AsyncFormDialog submission failed:', err);
                setError(formatApiError(err, 'Request failed'));
            }
        });
        submittingRef.current = false;
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return; // never close mid-flight
        dispatchLockWrap(() => onClose());
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="async-form-dialog-title"
            {...dialogProps}
            >
            <DialogTitle id="async-form-dialog-title">
                {title}
                {subtitle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent dividers>
                {children}
                {confirmRequired ? (
                    <TextField
                        margin="dense"
                        fullWidth
                        size="small"
                        label={confirmTextLabel}
                        value={confirmInput}
                        onChange={(e) => setConfirmInput(e.target.value)}
                        helperText={`Type "${confirmText}" to enable the ${submitLabel} button.`}
                        autoComplete="off"
                        sx={{ mt: 2 }}
                    />
                ) : null}
                {error ? (
                    <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>{cancelLabel}</Button>
                <ActionButton
                    variant="contained"
                    color={submitColor}
                    loading={submitting}
                    loadingPosition={submitIcon ? 'start' : undefined}
                    startIcon={submitIcon}
                    disabled={!primaryEnabled}
                    onClick={handleSubmit}
                >
                    {submitLabel}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
}

AsyncFormDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    children: PropTypes.node,
    submitLabel: PropTypes.string.isRequired,
    submitColor: PropTypes.string,
    submitIcon: PropTypes.node,
    cancelLabel: PropTypes.string,
    canSubmit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    confirmText: PropTypes.string,
    confirmTextLabel: PropTypes.string,
    dialogProps: PropTypes.object
};
